import { useState } from 'react';
import { api } from '../api/client';
import type { InvitablePerson } from '../pages/events/bulkImport/types';

interface InviteFamilyPromptProps {
  people: InvitablePerson[];
  onInvited: (memberId: string) => void;
}

type Outcome = { kind: 'success' | 'warning'; text: string };

export function InviteFamilyPrompt({ people, onInvited }: InviteFamilyPromptProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [relationship, setRelationship] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  if (people.length === 0) return null;

  function startInvite(person: InvitablePerson) {
    setExpandedId(person.memberId);
    setName(person.name);
    setEmail('');
    setRelationship('');
    setError('');
  }

  async function sendInvite(person: InvitablePerson) {
    if (!email.trim()) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.put(`/account/members/${person.memberId}`, {
        name: name.trim() || person.name,
        email: email.trim(),
        relationship: relationship.trim() || undefined,
        role: 'member',
      });
      const sentEmail = email.trim();
      if (data.emailDelivered === false) {
        setOutcome({ kind: 'warning', text: `${person.name} was added, but the invite email failed to send.` });
      } else if (data.emailDelivered === undefined) {
        setOutcome({ kind: 'success', text: `${person.name} already has an account — connected.` });
      } else {
        setOutcome({ kind: 'success', text: `Invitation sent to ${sentEmail}.` });
      }
      setExpandedId(null);
      onInvited(person.memberId);
      setTimeout(() => setOutcome(null), 4000);
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } }).response?.data?.error || "Couldn't send that invite — try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <p className="text-sm font-semibold text-slate-700 mb-2">Invite family from these memories</p>
      {outcome && (
        <p className={`text-sm mb-2 ${outcome.kind === 'success' ? 'text-green-700' : 'text-amber-700'}`}>
          {outcome.kind === 'success' ? '✓ ' : '⚠ '}{outcome.text}
        </p>
      )}
      <div className="space-y-2">
        {people.map(person => {
          const eventLabel = person.eventTitles.length > 1
            ? `${person.eventTitles[0]} and ${person.eventTitles.length - 1} more`
            : person.eventTitles[0];
          const isOpen = expandedId === person.memberId;
          return (
            <div key={person.memberId} className="bg-white rounded-xl border border-slate-100 overflow-hidden">
              <div className="flex items-center gap-3 px-3 py-2.5 flex-wrap">
                <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                  {(person.name[0] ?? '?').toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">
                    Invite {person.name} to add photos and record their own story for {eventLabel}
                  </p>
                </div>
                {!isOpen && (
                  <button
                    type="button"
                    onClick={() => startInvite(person)}
                    className="text-xs px-2.5 py-1 rounded-full border border-indigo-200 text-indigo-600 hover:bg-indigo-50 transition-colors flex-shrink-0"
                  >
                    Invite
                  </button>
                )}
              </div>

              {isOpen && (
                <div className="border-t border-slate-100 px-3 py-3 bg-indigo-50">
                  <div className="flex gap-2 flex-wrap mb-2">
                    <div className="flex-1 min-w-32">
                      <label htmlFor={`invite-name-${person.memberId}`} className="block text-xs font-medium text-slate-500 mb-1">Name</label>
                      <input
                        id={`invite-name-${person.memberId}`}
                        type="text" value={name} onChange={e => setName(e.target.value)}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                      />
                    </div>
                    <div className="flex-1 min-w-40">
                      <label htmlFor={`invite-email-${person.memberId}`} className="block text-xs font-medium text-slate-500 mb-1">Email</label>
                      <input
                        id={`invite-email-${person.memberId}`}
                        type="email" value={email} onChange={e => setEmail(e.target.value)}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                      />
                    </div>
                  </div>
                  <div className="mb-2">
                    <label htmlFor={`invite-relationship-${person.memberId}`} className="block text-xs font-medium text-slate-500 mb-1">Relationship</label>
                    <input
                      id={`invite-relationship-${person.memberId}`}
                      type="text" value={relationship} onChange={e => setRelationship(e.target.value)}
                      placeholder="e.g. Mother, Brother, Friend"
                      autoCorrect="off" autoCapitalize="off" spellCheck={false}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                    />
                  </div>
                  {error && <p className="text-red-600 text-xs mb-2">{error}</p>}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => sendInvite(person)}
                      disabled={!email.trim() || loading}
                      className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                    >
                      {loading ? 'Sending…' : 'Send invitation'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setExpandedId(null)}
                      className="px-3 py-1.5 bg-white text-slate-600 border border-slate-200 rounded-lg text-sm hover:bg-slate-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
